import { type FragmentType, graphql, useFragment } from '@typings/gql'
import { clsx } from 'clsx'
import React, { type FC } from 'react'

const MarketValue: FC<{ value: number; currency: string }> = ({
  value,
  currency,
}) => (
  <span className="before:px-1 before:content-['•']">
    {value.toLocaleString('fr-FR', {
      style: 'currency',
      currency,
    })}
  </span>
)

const MarketValueVariation: FC<{ value: number }> = ({ value }) => (
  <span className="before:px-1 before:content-['•']">
    <span
      className={clsx({
        'text-theme-error-main': value < 0,
        'text-amplification-iconic': value > 0,
      })}
    >
      {value > 0 ? '+' : ''}
      {value}%
    </span>
  </span>
)

type MarketRowProps = {
  content: FragmentType<typeof MarketRowFragment>
}

export const MarketRow = ({ content }: MarketRowProps) => {
  const {
    codeIsin,
    label,
    stockExchange,
    mnemo,
    url,
    variation,
    value,
    currency,
  } = useFragment(MarketRowFragment, content)

  return (
    <a className="hover:opacity-60" href={url}>
      <span className="text-100 text-amplification-black-premium group-[.is-dark-mode]/menu:text-white-50 font-normal!">
        {label}
      </span>
      <div className="text-085 text-amplification-black-premium/60 group-[.is-dark-mode]/menu:text-white-50/60 flex flex-wrap font-normal!">
        <span>{codeIsin}</span>
        {Boolean(mnemo) && (
          <span className="before:px-1 before:content-['•']">{mnemo}</span>
        )}
        {Boolean(stockExchange) && (
          <span className="before:px-1 before:content-['•']">
            {stockExchange}
          </span>
        )}
        {value !== null && <MarketValue currency={currency} value={value} />}
        {variation !== null && <MarketValueVariation value={variation} />}
      </div>
    </a>
  )
}

export const MarketRowFragment = graphql(`
  fragment MarketRow on MarketSearchItem {
    __typename
    codeIsin
    label
    stockExchange
    mnemo
    url
    variation
    value
    currency
  }
`)
